
import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Input, Row, Col, Modal, Form, Select, Radio, DatePicker, message } from 'antd';
import { EditOutlined, DeleteOutlined, SearchOutlined, PlusOutlined, PrinterOutlined } from '@ant-design/icons';
import { RiFolderTransferFill } from "react-icons/ri";
import moment from 'moment';
import api from '../api';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import SiteLogo from '../assests/logo.png'; // Ensure this path is correct

const { RangePicker } = DatePicker;

const Transactions = () => {
  const [searchText, setSearchText] = useState('');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentRecord, setCurrentRecord] = useState(null);
  const [form] = Form.useForm();
  const [data, setData] = useState([]);
  const [memberOptions, setMemberOptions] = useState([]);
  const [eventOptions, setEventOptions] = useState([]);
  const [selectedFilterEvent, setSelectedFilterEvent] = useState(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([fetchData(), fetchDataMember(), fetchDataEvent()])
      .then(() => setLoading(false))
      .catch(() => setLoading(false));
  }, []);

  const fetchData = async () => {
    try {
      const response = await api.get('/transaction/allTransaction');
      if (response.data && Array.isArray(response.data.data)) {
        setData(response.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch transaction data:', error);
      message.error('Failed to fetch transaction data. Please try again later.');
    }
  };

  const fetchDataMember = async () => {
    try {
      const response = await api.get('/member/get-all-members');
      if (response.data && Array.isArray(response.data.data)) {
        const options = response.data.data.map(v => ({
          value: v._id,
          label: v.Name || 'Unknown',
          accno: v.Accno || ''
        }));
        setMemberOptions(options);
      }
    } catch (error) {
      console.error('Failed to fetch member data:', error);
    }
  };

  const fetchDataEvent = async () => {
    try {
      const response = await api.get('/event');
      if (response.data && Array.isArray(response.data.data)) {
        const options = response.data.data.map(v => ({
          value: v.eventName,
          label: v.eventName || 'Unknown',
          eventDate: v.eventDate
        }));
        setEventOptions(options);
      }
    } catch (error) {
      console.error('Failed to fetch event data:', error);
    }
  };

  const getEventDate = (record) => {
    if (record?.eventDate) return record.eventDate;
    const recName = record?.eventName?.trim();
    const matched = eventOptions.find(e => (e.value && e.value.trim() === recName) || (e.label && e.label.trim() === recName));
    return matched?.eventDate || record?.createdAt;
  };

  const handleAdd = async (values) => {
    try {
      const payload = {
        ...values,
        Accno: memberOptions.find(m => m.value === values.memberId)?.accno || '',
        memberName: memberOptions.find(m => m.value === values.memberId)?.label || '',
        dateofpayment: values.dateofpayment ? (values.dateofpayment.toDate ? moment(values.dateofpayment.toDate()).format('YYYY-MM-DD') : moment(values.dateofpayment).format('YYYY-MM-DD')) : null,
        eventDate: values.eventDate ? (values.eventDate.toDate ? moment(values.eventDate.toDate()).format('YYYY-MM-DD') : moment(values.eventDate).format('YYYY-MM-DD')) : null,
      };
      const response = await api.post('/transaction/addTransaction', payload);
      if (response.status === 200 && response.data.success) {
        setData(prevData => [...prevData, response.data.data]);
        message.success('Transaction added successfully');
        setIsModalVisible(false);
        form.resetFields();
      }
    } catch (error) {
      message.error('Failed to add transaction.');
    }
  };

  const handleUpdate = async (values) => {
    try {
      if (!currentRecord || !currentRecord._id) return;
      const payload = {
        ...values,
        Accno: memberOptions.find(m => m.value === values.memberId)?.accno || '',
        memberName: memberOptions.find(m => m.value === values.memberId)?.label || '',
        dateofpayment: values.dateofpayment ? (values.dateofpayment.toDate ? moment(values.dateofpayment.toDate()).format('YYYY-MM-DD') : moment(values.dateofpayment).format('YYYY-MM-DD')) : null,
        eventDate: values.eventDate ? (values.eventDate.toDate ? moment(values.eventDate.toDate()).format('YYYY-MM-DD') : moment(values.eventDate).format('YYYY-MM-DD')) : null,
      };
      await api.put(`/transaction/updateTransaction/${currentRecord._id}`, payload);
      fetchData();
      message.success('Transaction updated successfully');
      setIsModalVisible(false);
      form.resetFields();
    } catch (error) {
      message.error('Failed to update transaction.');
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/transaction/deleteTransaction/${id}`);
      fetchData();
      message.success('Transaction deleted successfully');
    } catch (error) {
      message.error('Failed to delete transaction.');
    }
  };

  const handleEdit = (record) => {
    setEditMode(true);
    setCurrentRecord(record);
  
    // Find the memberId from memberOptions based on memberName
    const selectedMember = memberOptions.find((m) => m.label === record.memberName) || {};
    const matchedEventDate = getEventDate(record);
  
    form.setFieldsValue({
      memberId: selectedMember.value || '', // Set the memberId for the dropdown
      Accno: record.Accno || '',
      memberName: record.memberName || '',
      eventName: record.eventName || '',
      donation: record.donation || '',
      donateProd: record.donateProd || '',
      phone: record.phone || '',
      address: record.address || '',
      eventDate: matchedEventDate ? moment.utc(matchedEventDate) : null,
      paymentmode: record.paymentmode || '',
      dateofpayment: (record.dateofpayment && record.dateofpayment !== 'N/A') ? moment.utc(record.dateofpayment) : null,
      remarks: record.remarks || '',
    });
    setIsModalVisible(true);
  };
  const exportExcel = () => {
    const excelData = filteredData.map((record, index) => {
      const member = memberOptions.find((m) => m.label === record.memberName) || {};
      const acciCode = record.Accno || member.accno || "N/A";
      const evDate = getEventDate(record);
      return {
        "S.No": index + 1,
        "ACCI Code": acciCode,
        "Event Name": record.eventName || "N/A",
        "Member Name": member.label || record.memberName || "N/A",
        "Donation Type": record.donation || "N/A",
        "Amount/Product": record.donateProd || "N/A",
        "Payment Mode": record.paymentmode || "N/A",
        "Date of Payment": record.dateofpayment ? moment(record.dateofpayment).format('DD-MM-YYYY') : "N/A",
        "Event Date": evDate ? moment.utc(evDate).format('DD-MM-YYYY') : "N/A",
        "Remarks": record.remarks || "N/A",
      };
    });
  
    const totalRow = {
      "S.No": "",
      "ACCI Code": "",
      "Event Name": "",
      "Member Name": "Total",
      "Donation Type": "",
      "Amount/Product": filteredData.reduce((sum, record) => sum + (parseFloat(record.donateProd) || 0), 0).toFixed(2),
      "Payment Mode": "",
      "Date of Payment": "",
      "Event Date": "",
      "Remarks": "",
    };
  
    const worksheet = XLSX.utils.json_to_sheet([...excelData, totalRow]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Transactions");
    XLSX.writeFile(workbook, "transactions.xlsx");
  };

  const getBase64Image = async (url) => {
    try {
      const response = await fetch(url, { mode: "cors" });
      if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
      const blob = await response.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = () => reject(new Error("Failed to convert image to Base64"));
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error(`Failed to fetch image from ${url}:`, error);
      return null;
    }
  };

  const numberToWords = (num) => {
    const ones = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine"];
    const tens = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
    const teens = ["Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
    const units = ["", "Thousand", "Million", "Billion"];

    if (num === 0) return "Zero";

    const convertLessThanThousand = (n) => {
      if (n === 0) return "";
      if (n < 10) return ones[n];
      if (n < 20) return teens[n - 10];
      if (n < 100) return `${tens[Math.floor(n / 10)]} ${ones[n % 10]}`.trim();
      return `${ones[Math.floor(n / 100)]} Hundred ${convertLessThanThousand(n % 100)}`.trim();
    };

    const integerPart = Math.floor(num);
    let words = "";
    let n = integerPart;

    if (n === 0) words = "Zero";
    else {
      let i = 0;
      while (n > 0) {
        if (n % 1000 !== 0) {
          words = `${convertLessThanThousand(n % 1000)} ${units[i]} ${words}`.trim();
        }
        n = Math.floor(n / 1000);
        i++;
      }
    }

    return words;
  };

  const exportToPDF = () => {
    const doc = new jsPDF({ format: 'a5', unit: 'mm' });
    const pageWidth = doc.internal.pageSize.getWidth();

    doc.addImage(SiteLogo, 'PNG', 10, 5, 30, 20);
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text("Transactions Report", pageWidth / 2, 15, { align: "center" });

    doc.autoTable({
      startY: 30,
      head: [['S.No', 'ACCI Code', 'Member', 'Event', 'Event Date', 'Donation', 'Amount', 'Payment Mode']],
      body: filteredData.map((item, index) => [
        index + 1,
        item.Accno || memberOptions.find((m) => m.label === item.memberName)?.accno || 'Unknown',
        item.memberName,
        item.eventName,
        getEventDate(item) ? moment.utc(getEventDate(item)).format('DD-MM-YYYY') : 'N/A',
        item.donation,
        item.donateProd,
        item.paymentmode || 'N/A',
      ]),
      styles: { fontSize: 8 },
      margin: { left: 5, right: 5 },
      tableWidth: 'auto'
    });

    doc.save("Transactions.pdf");
  };

  const handlePrintSingleTransactionPDF = async (record) => {
    const doc = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a5",
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.height;
    const margin = 15;

    // LOGO - Top Left
    const acciLogoUrl = "/logo.png";
    const acciLogo = await getBase64Image(acciLogoUrl);
    if (acciLogo) {
      const imgWidth = 20;
      const imgHeight = 20;
      doc.addImage(acciLogo, "PNG", margin, 8, imgWidth, imgHeight);
    }

    // HEADER
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.text("AGAMUDAYAR", pageWidth / 2, 16, { align: "center" });

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.text("CHAMBER OF COMMERCE AND INDUSTRIES", pageWidth / 2, 22, { align: "center" });

    // TITLE
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text("TRANSACTION RECEIPT", pageWidth / 2, 32, { align: "center" });

    // DATE
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.text(`Date: ${new Date().toLocaleDateString()}`, margin, 40);

    // DETAILS TABLE
    const details = [
      ['ACCI Code', record.Accno || memberOptions.find((m) => m.label === record.memberName)?.accno || 'N/A'],
      ['Member Name', record.memberName || 'N/A'],
      ['Event', record.eventName || 'N/A'],
      ['Date of Payment', record.dateofpayment ? moment(record.dateofpayment).format('DD-MM-YYYY') : 'N/A'],
      ['Donation Type', record.donation || 'N/A'],
      ['Amount / Product', record.donateProd || 'N/A'],
      ['Payment Mode', record.paymentmode || 'N/A'],
    ];

    if (record.remarks && record.remarks !== 'N/A') {
      details.push(['Remarks', record.remarks]);
    }

    const tableStartY = 50; 
    doc.autoTable({
      startY: tableStartY,
      head: [['Description', 'Details']],
      body: details,
      theme: "grid",
      styles: { 
        fontSize: 9,
        cellPadding: 2,
      },
      headStyles: { 
        fillColor: [41, 128, 185], 
        textColor: 255,
        fontSize: 10,
        fontStyle: 'bold'
      },
      margin: { left: margin, right: margin },
      columnStyles: { 
        0: { cellWidth: 70, halign: 'left' },
        1: { cellWidth: 48, halign: 'right' } 
      },
    });

    let finalY = doc.lastAutoTable.finalY || tableStartY;
    
    if (finalY > pageHeight - 50) {
      doc.addPage();
      finalY = 20;
    }

    // AMOUNT IN WORDS
    if (record.donation === 'Amount' && !isNaN(parseFloat(record.donateProd))) {
      doc.setFontSize(10);
      doc.setFont("helvetica", "bold");
      const totalAmount = parseFloat(record.donateProd);
      doc.text(`In words: Rupees ${numberToWords(Math.round(totalAmount))} Only`, margin, finalY + 10);
    }

    // FOOTER
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      const footerY = pageHeight - 30;
      
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.text("Thank you for your payment!", pageWidth / 2, footerY, { align: "center" });

      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.text("Administrative Office", margin, footerY + 6);
      doc.text("Plot No - 5 West, Marthupandiyar 2nd Cross Street,", margin, footerY + 10);
      doc.text("Viswanathapuram, Madurai - 625 014", margin, footerY + 14);
      doc.text("Phone: +91 96000 35625", margin, footerY + 18);
      
      doc.text(`Page ${i} of ${pageCount}`, pageWidth - margin - 10, footerY + 18);
    }

    const pdfBlob = doc.output('blob');
    const pdfURL = URL.createObjectURL(pdfBlob);
    window.open(pdfURL, '_blank');
  };

  const filteredData = data.filter(item => {
    const matchesEvent = selectedFilterEvent ? item.eventName === selectedFilterEvent : true;
    const matchesSearch = searchText
      ? [
          item.Accno,
          item.memberName,
          item.eventName,
          item.donation,
          item.donateProd,
          item.paymentmode,
          item.remarks
        ].some(field => 
          field && field.toString().toLowerCase().includes(searchText.toLowerCase())
        )
      : true;
    return matchesEvent && matchesSearch;
  });

  const columns = [
    {
      title: 'S.No',
      key: 'sno',
      render: (text, record, index) => (pagination.current - 1) * pagination.pageSize + index + 1,
      width: '5%',
    },
    { 
      title: 'ACCI Code', 
      key: 'Accno', 
      width: '10%',
      render: (_, record) => record.Accno || memberOptions.find((m) => m.label === record.memberName)?.accno || 'N/A'
    },
    { title: 'Member Name', dataIndex: 'memberName', key: 'memberName', width: '15%' },
    { title: 'Event Name', dataIndex: 'eventName', key: 'eventName', width: '15%' },
    {
      title: 'Event Date',
      dataIndex: 'eventDate',
      key: 'eventDate',
      width: '10%',
      render: (_, record) => {
        const dateVal = getEventDate(record);
        return dateVal ? moment.utc(dateVal).format('DD-MM-YYYY') : 'N/A';
      },
    },
    { title: 'Donation', dataIndex: 'donation', key: 'donation', width: '10%' },
    { title: 'Amount', dataIndex: 'donateProd', key: 'donateProd', width: '10%' },
    { title: 'Payment Mode', dataIndex: 'paymentmode', key: 'paymentmode', width: '10%' },
    {
      title: 'Date of Payment',
      dataIndex: 'dateofpayment',
      key: 'dateofpayment',
      width: '10%',
      render: (text) => (text && text !== 'N/A') ? moment.utc(text).format('DD-MM-YYYY') : 'N/A',
    },
    {
      title: 'Remarks',
      dataIndex: 'remarks',
      key: 'remarks',
      width: '12%',
      render: (text) => text || 'N/A',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button icon={<EditOutlined />} onClick={() => handleEdit(record)} />
          <Button icon={<DeleteOutlined />} danger onClick={() => handleDelete(record._id)} />
          <Button icon={<PrinterOutlined />} onClick={() => handlePrintSingleTransactionPDF(record)} />
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '20px' }}>
      <h2><RiFolderTransferFill /> Transactions</h2>
      <Row gutter={16} style={{ marginBottom: '20px' }}>
        <Col span={10}>
          <Input
            placeholder="Search transactions"
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: '50%' }}
          />
          <Button style={{ marginLeft: 10 }} onClick={exportToPDF}>Export PDF</Button>
          <Button style={{ marginLeft: 10 }} onClick={exportExcel}>Export Excel</Button>
        </Col>
        <Col span={8}>
          <Select
            placeholder="Filter by Event Name"
            allowClear
            style={{ width: '100%' }}
            onChange={(value) => setSelectedFilterEvent(value || null)}
            options={eventOptions}
          />
        </Col>
        <Col span={6} style={{ textAlign: 'right' }}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => { setIsModalVisible(true); setEditMode(false); form.resetFields(); }}
          >
            Add Transaction
          </Button>
        </Col>
      </Row>
      <Table
        columns={columns}
        dataSource={filteredData}
        rowKey="_id"
        pagination={pagination}
        loading={loading}
        onChange={(newPagination) => setPagination(newPagination)}
      />
      <Modal
        title={editMode ? 'Edit Transaction' : 'Add New Transaction'}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        onOk={() => form.submit()}
        width={800}
      >
        <Form form={form} onFinish={editMode ? handleUpdate : handleAdd} layout="vertical">
          {/* Row 1 */}
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="eventName" label="Event" rules={[{ required: true }]}>
                <Select
                  showSearch
                  optionFilterProp="children"
                  onChange={(val) => {
                    const selectedEv = eventOptions.find(e => e.value === val);
                    if (selectedEv && selectedEv.eventDate) {
                      form.setFieldsValue({
                        eventDate: moment.utc(selectedEv.eventDate)
                      });
                    }
                  }}
                  filterOption={(input, option) =>
                    (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {eventOptions.map(opt => (
                    <Select.Option key={opt.value} value={opt.value}>{opt.label}</Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="memberId" label="Member" rules={[{ required: true }]}>
                <Select
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) => {
                    const nameMatch = (option?.children ?? '').toLowerCase().includes(input.toLowerCase());
                    const accnoMatch = (option?.accno ?? '').toLowerCase().includes(input.toLowerCase());
                    return nameMatch || accnoMatch;
                  }}
                >
                  {memberOptions.map(opt => (
                    <Select.Option key={opt.value} value={opt.value} accno={opt.accno}>
                      {`${opt.accno} - ${opt.label}`}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          {/* Row 2 */}
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="donation" label="Donation" rules={[{ required: true }]}>
                <Radio.Group>
                  <Radio value="Amount">Amount</Radio>
                  <Radio value="Product">Product</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="donateProd" label="Product/Amount">
                <Input />
              </Form.Item>
            </Col>
          </Row>

          {/* Row 3 */}
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="paymentmode" label="Payment Mode" rules={[{ required: true }]}>
                <Select>
                  <Select.Option value="Cash">Cash</Select.Option>
                  <Select.Option value="UPI">UPI</Select.Option>
                  <Select.Option value="Bank">Bank</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="dateofpayment" label="Date of Payment" rules={[{ required: true }]}>
                <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
              </Form.Item>
            </Col>
          </Row>

          {/* Row 4 */}
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="phone" label="Phone">
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="address" label="Address">
                <Input />
              </Form.Item>
            </Col>
          </Row>

          {/* Row 5 */}
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="eventDate" label="Event Date" rules={[{ required: true }]}>
                <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="remarks" label="Remarks">
                <Input />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default Transactions;
